import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';
import useGameState from '../gameState';
import { isMobileDevice } from '../device';
import {
  getChatState,
  sendMessage,
  addAssistantMessage,
  getThreads,
  getThreadMessages,
  markThreadRead,
  skipReveal,
  visibleChars,
  isRevealing,
} from './chat';
import {
  ensureFirstChatStory,
  FIRST_CHAT_STORY_LINE,
  OFFLINE_CHAT_STORY_LINE,
  RESUMED_CHAT_STORY_LINE,
} from '../firstSession';
import { COPY } from '../../content/copy';

/**
 * CHAT app window (ADR-0021 / DOPEOS-SYSTEM-HOME-CHAT.md section 3).
 *
 * One window on the DopeOS desktop: title bar + close, RECENTS (a column on
 * desktop, a tab strip on mobile), the thread transcript scrolling inside the
 * window, quick replies, an input anchored to the bottom and a status line.
 * Canvas only; the single DOM node is an invisible input that summons the soft
 * keyboard on touch devices.
 */

const CELL_W = 8;
const LINE_H = 20;
const MSG_GAP = 10;
const MAX_INPUT = 150;
const DOM_INPUT_ID = 'dcw-chat-input';

// Transcript geometry: a fixed speaker column so every message body starts at
// the same x, and a reserved right column for the dim clock timestamp.
const SPEAKER_COLS = 7; // "VINNIE" / "SYSTEM" fit in 7 cells
const SPEAKER_COL_W = SPEAKER_COLS * CELL_W;
const COL_GAP = 8;
const TEXT_INSET = SPEAKER_COL_W + COL_GAP;
const TIME_COLS = 5; // "HH:MM"
const TIME_W = TIME_COLS * CELL_W;
const RIGHT_PAD = 8;
const READABLE_MAX_CHARS = 48;

const SPEAKERS = {
  user: {
    label: 'YOU',
    color: TERMINAL.TEXT_BRIGHT,
    labelColor: TERMINAL.TEXT_BRIGHT,
    player: true,
  },
  assistant: {
    label: 'VINNIE',
    color: TERMINAL.TEXT,
    labelColor: TERMINAL.GREEN_LIGHT,
    player: false,
  },
  system: {
    label: 'SYSTEM',
    color: TERMINAL.TEXT_DIM,
    labelColor: TERMINAL.TEXT_DIM,
    player: false,
  },
};

/** Speaker metadata for a message role. Unknown roles read as Vinnie. */
export function speakerFor(role) {
  return SPEAKERS[role] || SPEAKERS.assistant;
}

/** Dim clock label for a message timestamp (`HH:MM`, local time). */
export function formatClock(ts) {
  if (!ts) return '';
  const date = new Date(ts);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

let openedAt = 0;
let lastRenderAt = 0;

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function fit(value, maxChars) {
  return value.length <= maxChars ? value : value.slice(0, Math.max(0, maxChars));
}

/**
 * Subtitle for a recents row. Typing reads the copy registry; threads with no
 * data render nothing (ADR-0021).
 */
export function threadSubtitle(thread) {
  if (thread.typing) return COPY.comms.typing;
  return thread.last ? thread.last.text : '';
}

/** Word-wrap that keeps each line's offset into the source string. */
export function wrapLines(str, max) {
  const out = [];
  let i = 0;
  while (i < str.length) {
    let end = Math.min(str.length, i + max);
    if (end < str.length) {
      const space = str.lastIndexOf(' ', end);
      if (space > i) end = space;
    }
    out.push({ text: str.slice(i, end), start: i });
    i = end;
    while (str[i] === ' ') i += 1;
  }
  return out;
}

/** Window geometry. Pure so tests can check targets and bounds. */
export function layoutChat(width, height) {
  const mobile = width < 500;
  const win = mobile
    ? { x: 0, y: 0, w: width, h: height }
    : { x: 6, y: 6, w: width - 12, h: height - 12 };
  const titleH = mobile ? 44 : 22;
  const statusH = mobile ? 20 : 18;
  const rowH = mobile ? 44 : 28;
  const status = { x: win.x, y: win.y + win.h - statusH, w: win.w, h: statusH };
  const input = { y: status.y - rowH - 4, h: rowH };
  const quick = { y: input.y - rowH - 4, h: rowH };
  const title = { x: win.x, y: win.y, w: win.w, h: titleH };
  const close = mobile
    ? { x: win.x + win.w - 48, y: win.y, w: 44, h: 44 }
    : { x: win.x + win.w - 34, y: win.y + 1, w: 28, h: titleH - 2 };

  let recents;
  let pane;
  if (mobile) {
    // 48px strip so each recents row (strip minus 4px padding) is a 44px target.
    recents = { x: win.x, y: title.y + titleH, w: win.w, h: 48, rowW: 96 };
    pane = { x: win.x + 8, w: win.w - 16, top: recents.y + recents.h + 4 };
  } else {
    recents = { x: win.x, y: title.y + titleH, w: 150, h: status.y - (title.y + titleH), rowH: 28 };
    pane = { x: win.x + recents.w + 8, w: win.w - recents.w - 16, top: title.y + titleH + 22 };
  }
  input.x = pane.x;
  input.w = pane.w - (mobile ? 60 : 64);
  input.send = { x: pane.x + pane.w - (mobile ? 56 : 56), y: input.y, w: 56, h: rowH };
  quick.x = pane.x;
  quick.w = pane.w;
  const transcript = { x: pane.x, y: pane.top, w: pane.w, h: quick.y - 4 - pane.top };
  return { mobile, win, title, close, status, recents, pane, quick, input, transcript };
}

function quickActions(state, mobile) {
  const defaults = [
    { label: 'LOAN', fullText: 'I need a loan' },
    { label: 'DEBT', fullText: 'Check my debt' },
    { label: 'PAY', fullText: 'I want to pay back' },
    { label: 'FAVOR', fullText: 'Do me a favor' },
  ];
  const list = state.dynamicQuickActions && state.dynamicQuickActions.length
    ? state.dynamicQuickActions
    : defaults;
  return list.slice(0, mobile ? 3 + 1 : 4);
}

// ---- Soft keyboard bridge (touch only) --------------------------------

function needsDomInput() {
  try {
    return typeof document !== 'undefined' && isMobileDevice();
  } catch {
    return false;
  }
}

function getDomInput(create) {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(DOM_INPUT_ID);
  if (!el && create) {
    el = document.createElement('input');
    el.id = DOM_INPUT_ID;
    el.type = 'text';
    el.autocomplete = 'off';
    el.autocapitalize = 'sentences';
    el.maxLength = MAX_INPUT;
    el.setAttribute('enterkeyhint', 'send');
    el.setAttribute('aria-label', 'Message to Vinnie');
    // Invisible and pinned to the bottom so the browser keeps it above the
    // keyboard. 16px stops iOS from zooming on focus.
    el.style.cssText =
      'position:fixed;left:0;bottom:0;width:1px;height:1px;opacity:0;border:0;padding:0;font-size:16px;z-index:5;';
    el.addEventListener('input', () => {
      getChatState().inputText = el.value.replace(/[^\x20-\x7e]/g, '').slice(0, MAX_INPUT);
      getChatState().inputActive = true;
    });
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const state = getChatState();
        if (state.inputText) sendMessage(state.inputText);
        else skipReveal();
        el.value = '';
      }
    });
    document.body.appendChild(el);
  }
  return el;
}

function focusInput() {
  const state = getChatState();
  state.inputActive = true;
  if (needsDomInput()) {
    const el = getDomInput(true);
    if (el) {
      el.value = state.inputText;
      el.focus({ preventScroll: true });
    }
  }
}

function releaseInput() {
  const el = getDomInput(false);
  if (el) el.blur();
}

// ---- Transcript ---------------------------------------------------------

/**
 * Turn messages into transcript rows. Speaker, clock and player emphasis are
 * carried on the row, never baked into the text, so the body column stays
 * aligned no matter who is speaking.
 */
export function buildLines(messages, typing, maxChars) {
  const lines = [];
  messages.forEach((message) => {
    const speaker = speakerFor(message.role);
    const wrapped = wrapLines(message.text, maxChars);
    const body = wrapped.length ? wrapped : [{ text: '', start: 0 }];
    const shown = visibleChars(message);
    body.forEach((line, i, all) => {
      lines.push({
        text: line.text,
        shown: Math.max(0, Math.min(line.text.length, shown - line.start)),
        color: speaker.color,
        label: speaker.label,
        labelColor: speaker.labelColor,
        player: speaker.player,
        first: i === 0,
        last: i === all.length - 1,
        blockH: i === 0 ? body.length * LINE_H + MSG_GAP : 0,
        time: i === 0 ? formatClock(message.timestamp) : '',
      });
    });
  });
  if (typing) {
    lines.push({
      typing: true,
      text: '',
      shown: 0,
      color: TERMINAL.TEXT_BRIGHT,
      label: 'VINNIE',
      labelColor: TERMINAL.GREEN_LIGHT,
      player: false,
      first: true,
      last: true,
      blockH: LINE_H + MSG_GAP,
      time: '',
    });
  }
  return lines;
}

function drawTranscript(ctx, time, state, box, mobile) {
  const maxChars = Math.max(
    8,
    Math.min(
      READABLE_MAX_CHARS,
      Math.floor((box.w - TEXT_INSET - TIME_W - RIGHT_PAD) / CELL_W),
    ),
  );
  const messages = getThreadMessages();
  const typing = state.isTyping && state.activeThread === 'vinnie';
  const lines = buildLines(messages, typing, maxChars);
  const textX = box.x + TEXT_INSET;
  const timeX = box.x + box.w - RIGHT_PAD - TIME_W;

  let total = 0;
  lines.forEach((line) => { total += LINE_H + (line.last ? MSG_GAP : 0); });
  state.maxScroll = Math.max(0, total - box.h);
  state.scrollOffset = Math.max(0, Math.min(state.scrollOffset, state.maxScroll));

  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  let y = total < box.h ? box.y + total : box.y + box.h + state.scrollOffset;
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    const line = lines[i];
    y -= LINE_H + (line.last ? MSG_GAP : 0);
    const top = y + (line.last ? MSG_GAP : 0);
    if (top + LINE_H < box.y || top > box.y + box.h) continue;
    if (line.typing) {
      const dots = '.'.repeat(1 + (Math.floor(time * 3) % 3));
      text(ctx, line.label, box.x + 8, top, line.labelColor);
      text(ctx, `IS TYPING${dots}`, textX, top, line.color);
    } else {
      if (line.first) {
        text(ctx, line.label, box.x + 8, top, line.labelColor);
        if (line.time) text(ctx, line.time, timeX, top, TERMINAL.TEXT_DIM);
        if (line.player) {
          ctx.fillStyle = TERMINAL.TEXT_BRIGHT;
          ctx.fillRect(box.x + 2, top + 2, 2, Math.max(2, line.blockH - MSG_GAP - 4));
        }
      }
      if (line.shown > 0) {
        text(ctx, line.text.slice(0, line.shown), textX, top, line.color);
      }
    }
  }
  ctx.restore();

  if (state.maxScroll > 0 && !mobile) {
    const progress = 1 - state.scrollOffset / state.maxScroll;
    DopeUI.Scrollbar(ctx, box.x + box.w - 6, box.y, 6, box.h, progress, box.h / total);
  }
  if (!messages.length) {
    text(ctx, 'NO SIGNAL. TYPE TO SPEAK.', textX, box.y + 4, TERMINAL.TEXT_DIM);
  }
}

// ---- Window -------------------------------------------------------------

function drawRecents(ctx, L) {
  const threads = getThreads();
  const state = getChatState();
  const { recents } = L;

  const select = (id) => {
    state.activeThread = id;
    state.scrollOffset = 0;
    markThreadRead(id);
  };
  const draw = (thread, x, y, w, h) => {
    const active = thread.id === state.activeThread;
    ctx.fillStyle = active ? TERMINAL.GREEN_DARK : TERMINAL.GREEN_DARKEST;
    ctx.fillRect(x, y, w, h);
    ctx.strokeStyle = active ? TERMINAL.TEXT : TERMINAL.GREEN;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    const badge = thread.unread ? ` (${thread.unread})` : '';
    const label = `${active ? '> ' : ''}${thread.name}${badge}`;
    text(ctx, fit(label, Math.floor((w - 12) / CELL_W)), x + 6, y + (h > 30 ? 6 : 6), active ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    if (h > 30) {
      const sub = threadSubtitle(thread);
      text(ctx, fit(sub, Math.floor((w - 12) / CELL_W)), x + 6, y + 24, TERMINAL.TEXT_DIM);
    }
    interactiveRegistry.register({
      id: `chat-thread-${thread.id}`, x, y, w, h, skipInflation: true, onClick: () => select(thread.id),
    });
  };

  if (L.mobile) {
    ctx.fillStyle = TERMINAL.GREEN_DARKEST;
    ctx.fillRect(recents.x, recents.y, recents.w, recents.h);
    text(ctx, 'RECENTS', recents.x + 8, recents.y + 14, TERMINAL.TEXT_DIM);
    let x = recents.x + 8 + 8 * CELL_W + 8;
    threads.forEach((thread) => {
      draw(thread, x, recents.y + 2, recents.rowW, recents.h - 4);
      x += recents.rowW + 6;
    });
  } else {
    ctx.fillStyle = TERMINAL.GREEN_DARKEST;
    ctx.fillRect(recents.x + 1, recents.y, recents.w, recents.h);
    ctx.strokeStyle = TERMINAL.GREEN;
    ctx.beginPath();
    ctx.moveTo(recents.x + recents.w + 0.5, recents.y);
    ctx.lineTo(recents.x + recents.w + 0.5, recents.y + recents.h);
    ctx.stroke();
    text(ctx, 'RECENTS', recents.x + 8, recents.y + 3, TERMINAL.TEXT_DIM);
    threads.forEach((thread, i) => {
      draw(thread, recents.x + 4, recents.y + 22 + i * (recents.rowH * 2 + 4), recents.w - 8, recents.rowH * 2);
    });
  }
}

function drawScrollButtons(ctx, L, state) {
  if (!L.mobile || state.maxScroll <= 0) return;
  const { recents } = L;
  const step = 96;
  [
    { id: 'chat-scroll-up', label: '^', x: recents.x + recents.w - 100, delta: step },
    { id: 'chat-scroll-down', label: 'v', x: recents.x + recents.w - 50, delta: -step },
  ].forEach((b) => {
    DopeUI.Button(ctx, b.x, recents.y, 44, 44, b.label, 'normal');
    interactiveRegistry.register({
      id: b.id, x: b.x, y: recents.y, w: 44, h: 44, skipInflation: true,
      onClick: () => {
        state.scrollOffset = Math.max(0, Math.min(state.scrollOffset + b.delta, state.maxScroll));
      },
    });
  });
}

function drawInput(ctx, time, L, state) {
  const { input, mobile } = L;
  ctx.fillStyle = state.inputActive ? TERMINAL.GREEN_DARKEST : TERMINAL.BLACK;
  ctx.fillRect(input.x, input.y, input.w, input.h);
  ctx.strokeStyle = state.inputActive ? TERMINAL.TEXT : TERMINAL.GREEN;
  ctx.lineWidth = 1;
  ctx.strokeRect(input.x + 0.5, input.y + 0.5, input.w - 1, input.h - 1);

  const maxChars = Math.floor((input.w - 24) / CELL_W);
  const cursor = state.inputActive && Math.floor(time * 2) % 2 === 0 ? '_' : ' ';
  const typed = state.inputText;
  const shown = typed
    ? `${typed.length > maxChars ? typed.slice(typed.length - maxChars) : typed}${cursor}`
    : 'Type a message...';
  text(ctx, shown, input.x + 8, input.y + (input.h - LINE_H) / 2, typed ? TERMINAL.TEXT : TERMINAL.TEXT_DIM);
  interactiveRegistry.register({
    id: 'comms-input', x: input.x, y: input.y, w: input.w, h: input.h, skipInflation: true,
    onClick: focusInput,
  });

  const send = input.send;
  DopeUI.Button(ctx, send.x, send.y, send.w, send.h, mobile ? '>' : 'SEND', typed ? 'hover' : 'disabled');
  interactiveRegistry.register({
    id: 'comms-send', x: send.x, y: send.y, w: send.w, h: send.h, skipInflation: true,
    onClick: () => {
      if (state.inputText) sendMessage(state.inputText);
      const el = getDomInput(false);
      if (el) el.value = '';
    },
  });
}

function formatLink(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const mm = String(Math.floor(total / 60)).padStart(2, '0');
  const ss = String(total % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

export function renderCommsWindow(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  const L = layoutChat(safe.width, safe.height);
  const state = getChatState();
  const now = Date.now();
  if (now - lastRenderAt > 1500) openedAt = now;
  lastRenderAt = now;
  markThreadRead();

  // First Chat open starts the story (once) and syncs the installed apps the
  // opening beat created. The trigger ignores duplicate renders/reopens and
  // only reports the outcome that owns the first line (LK-54-03/04/05).
  ensureFirstChatStory({
    onStarted: (outcome) => {
      if (!outcome) return;
      if (outcome.resumed) addAssistantMessage(RESUMED_CHAT_STORY_LINE);
      else if (outcome.started) addAssistantMessage(FIRST_CHAT_STORY_LINE);
      else addAssistantMessage(OFFLINE_CHAT_STORY_LINE);
    },
  }).catch(() => {
    /* Chat stays usable even if the first-session bridge fails */
  });

  // Desktop behind the window.
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  // Window frame + title bar.
  const { win, title, close } = L;
  DopeUI.Panel(ctx, win.x, win.y, win.w, win.h, null);
  ctx.fillStyle = TERMINAL.GREEN_DARK;
  ctx.fillRect(title.x + 1, title.y + 1, title.w - 2, title.h - 1);
  ctx.strokeStyle = TERMINAL.GREEN;
  ctx.beginPath();
  ctx.moveTo(title.x, title.y + title.h + 0.5);
  ctx.lineTo(title.x + title.w, title.y + title.h + 0.5);
  ctx.stroke();
  text(
    ctx,
    '[ CHAT // LIVE ]',
    title.x + 8,
    title.y + (title.h - LINE_H) / 2,
    TERMINAL.TEXT,
  );
  DopeUI.Button(ctx, close.x, close.y, close.w, close.h, 'X', 'normal');
  interactiveRegistry.register({
    id: 'comms-close', x: close.x, y: close.y, w: close.w, h: close.h, skipInflation: true,
    onClick: () => { releaseInput(); navigateTo('main_menu'); },
  });

  drawRecents(ctx, L);
  drawScrollButtons(ctx, L, state);

  const { pane, transcript, quick } = L;
  if (!L.mobile) {
    text(ctx, 'CHAT // OPEN LINE', pane.x + 6, title.y + title.h + 3, TERMINAL.TEXT_DIM);
    if (state.isTyping && state.activeThread === 'vinnie') {
      const dots = '.'.repeat(1 + (Math.floor(time * 3) % 3));
      const label = `VINNIE IS TYPING${dots}`;
      text(ctx, label, pane.x + pane.w - label.length * CELL_W - 6, title.y + title.h + 3, TERMINAL.TEXT_BRIGHT);
    }
  }

  drawTranscript(ctx, time, state, transcript, L.mobile);
  // Tap/click the transcript to fast-forward Vinnie's text.
  interactiveRegistry.register({
    id: 'chat-transcript', x: transcript.x, y: transcript.y, w: transcript.w, h: transcript.h,
    skipInflation: true, focusable: false,
    onClick: () => { skipReveal(); },
  });

  // Contextual replies for Vinnie's thread.
  const actions = quickActions(state, L.mobile);
  const gap = 6;
  const bw = Math.floor((quick.w - gap * (actions.length - 1)) / actions.length);
  actions.forEach((action, index) => {
    const bx = quick.x + index * (bw + gap);
    const label = fit(action.label, Math.floor((bw - 8) / CELL_W));
    DopeUI.Button(ctx, bx, quick.y, bw, quick.h, label, 'normal');
    interactiveRegistry.register({
      id: `comms-${action.label}`, x: bx, y: quick.y, w: bw, h: quick.h, skipInflation: true,
      onClick: () => sendMessage(action.fullText),
    });
  });

  drawInput(ctx, time, L, state);

  // Mirror the canvas input into the soft-keyboard bridge.
  const el = getDomInput(false);
  if (el && el.value !== state.inputText) el.value = state.inputText;

  // Status line.
  const st = L.status;
  ctx.fillStyle = TERMINAL.GREEN_DARK;
  ctx.fillRect(st.x + 1, st.y, st.w - 2, st.h - 1);
  const online = (useGameState.getState().core || {}).status === 'online';
  const link = online ? `LINKED // ${formatLink(now - openedAt)}` : 'OFFLINE // LOCAL SHELL';
  text(ctx, link, st.x + 8, st.y + 2, online ? TERMINAL.GREEN_LIGHT : TERMINAL.TEXT_DIM);
  if (!L.mobile) {
    const hint = isRevealing(now) ? 'ENTER SKIP' : 'ENTER SEND   ESC CLOSE';
    text(ctx, hint, st.x + st.w - hint.length * CELL_W - 8, st.y + 2, TERMINAL.TEXT_DIM);
  }
}

registerView('comms_window', renderCommsWindow);

export default 'comms_window';
